// src/components/Dashboard/AdminUserCreation.jsx
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  DialogActions,
  Grid
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const AdminUserCreation = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    nome: '',
    username: '',
    email: '',
    senha: '',
    tipo: ''
  });

  const tiposAdministrativos = [
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'direcao_cientifica', label: 'Direção Científica' },
    { value: 'arguente', label: 'Arguente' },
    { value: 'presidente', label: 'Presidente' },
    { value: 'revisor_projeto', label: 'Revisor de Projeto' }
  ];

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, senha: password }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!formData.nome || !formData.username || !formData.senha || !formData.tipo) {
      setError('Nome, username, senha e tipo são obrigatórios.');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        isAdminRegistration: true,
        metodoCadastro: formData.email ? 'email' : 'username'
      };

      const response = await api.post('/auth/registro', payload);
      
      setSuccess(`Usuário ${formData.tipo} "${formData.nome}" criado com sucesso! 
        Username: ${formData.username} 
        Senha: ${formData.senha}`);
      
      // Limpar formulário
      setFormData({
        nome: '',
        username: '',
        email: '',
        senha: '',
        tipo: ''
      });

      // Fechar após 3 segundos
      setTimeout(() => {
        setOpenDialog(false);
        setSuccess('');
      }, 3000);

    } catch (err) {
      console.error('Erro ao criar usuário:', err);
      setError(err.response?.data?.mensagem || 'Erro ao criar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpenDialog(false);
    setError('');
    setSuccess('');
    setFormData({
      nome: '',
      username: '',
      email: '',
      senha: '',
      tipo: ''
    });
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Usuários Administrativos
      </Typography>
      
      <Card>
        <CardContent sx={{ textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Criar Usuário Administrativo
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Crie supervisores, arguentes, presidentes e outros usuários do sistema
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setOpenDialog(true)}
            size="large"
          >
            Criar Usuário Administrativo
          </Button>
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Criar Usuário Administrativo</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Nome Completo"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  disabled={loading}
                  helperText="Use apenas letras, números e pontos"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email (opcional)"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo de Usuário</InputLabel>
                  <Select
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {tiposAdministrativos.map((tipo) => (
                      <MenuItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={8}>
                <TextField
                  required
                  fullWidth
                  label="Senha"
                  name="senha"
                  type="text"
                  value={formData.senha}
                  onChange={handleChange}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={4}>
                <Button 
                  variant="outlined" 
                  onClick={generateRandomPassword}
                  disabled={loading}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  Gerar Senha
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Criar Usuário'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUserCreation;